<template>
  <div class="stats-filter-bar flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
    <!-- Range: a segmented control rather than a dropdown. It is the filter
         people change most, so it should cost one click, not two. -->
    <div class="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
      <button
        v-for="r in RANGES" :key="r.value"
        type="button"
        @click="store.setFilters({ range: r.value })"
        class="px-2.5 py-1 text-xs font-semibold rounded-md transition-colors"
        :class="store.filters.range === r.value
          ? 'bg-white dark:bg-[#1C2128] text-gray-900 dark:text-white shadow-sm'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
      >{{ r.label }}</button>
    </div>

    <div v-if="store.filters.range === 'custom'" class="flex items-center gap-1.5">
      <DatePicker :model-value="store.filters.startDate" @update:model-value="v => store.setFilters({ startDate: v })" placeholder="From" input-class="px-2.5 py-1 text-xs rounded-lg w-32" />
      <span class="text-xs text-gray-400">to</span>
      <DatePicker :model-value="store.filters.endDate" @update:model-value="v => store.setFilters({ endDate: v })" placeholder="To" input-class="px-2.5 py-1 text-xs rounded-lg w-32" />
    </div>

    <StatsFilterDropdown label="Priority" :options="PRIORITY_OPTIONS" :selected="store.filters.priorities" @update="v => store.setFilters({ priorities: v })" />
    <StatsFilterDropdown label="Colour" :options="COLOR_OPTIONS" :selected="store.filters.colors" swatches @update="v => store.setFilters({ colors: v })" />

    <StatsSelect
      label="Type"
      :model-value="store.filters.taskType"
      :options="TASK_TYPE_OPTIONS"
      default-value="all"
      @update:model-value="v => store.setFilters({ taskType: v })"
    />

    <label class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
      <input
        type="checkbox"
        :checked="store.filters.includeCanceled"
        @change="store.setFilters({ includeCanceled: $event.target.checked })"
        class="rounded text-blue-600 focus:ring-blue-500"
      />
      Include cancelled
    </label>

    <button
      v-if="store.hasActiveFilters"
      type="button"
      @click="store.resetFilters()"
      class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
    >Reset</button>

    <div class="ml-auto"><StatsExportMenu /></div>
  </div>
</template>

<script setup>
import { useTaskStatsStore } from 'stores/taskStats.js'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'
import StatsFilterDropdown from './StatsFilterDropdown.vue'
import StatsSelect from './StatsSelect.vue'
import StatsExportMenu from './StatsExportMenu.vue'
import { TASK_COLORS, TASK_COLOR_HEX } from '../taskColors.js'
import { PRIORITY_ORDER, PRIORITY_META } from '../taskPriorities.js'

const store = useTaskStatsStore()

const RANGES = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '12m', label: '12 months' },
  { value: 'custom', label: 'Custom' },
]

// The descriptions are the reason this is not a native select. "Recurring"
// especially does not mean what people assume: the stats count the individual
// occurrences a schedule produces, never the schedule row itself, which is not
// a task anyone works on. Saying so here is the only place that
// misunderstanding gets corrected at the moment it matters.
const TASK_TYPE_OPTIONS = [
  {
    value: 'all',
    label: 'All types',
    description: 'One time tasks and recurring occurrences counted together.',
  },
  {
    value: 'one_time',
    label: 'One time',
    description: 'Standalone tasks you created yourself, each with a single due date.',
  },
  {
    value: 'recurring',
    label: 'Recurring',
    description: 'The individual occurrences a repeating schedule produces. The schedule itself is not counted.',
  },
]

const PRIORITY_OPTIONS = PRIORITY_ORDER.map(v => ({ value: v, label: PRIORITY_META[v].label }))
const COLOR_OPTIONS = TASK_COLORS.map(c => ({ value: c.value, label: c.label, hex: TASK_COLOR_HEX[c.value] }))
</script>
